/**
 * Google Tag Manager dataLayer Helper
 * All tracking should flow through dataLayer for centralized tag management
 */
import { createLogger } from "@/lib/logger";
import { buildPaywallSourceProperties, normalizePaywallSource } from "./paywall-source";

const logger = createLogger("gtm-datalayer");

export type PaywallSurface =
  | "agent_payment_card"
  | "cohort_purchase_modal"
  | "lifetime_page"
  | "paywall_page"
  | "pricing_section";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Initialize dataLayer if it doesn't exist
 */
export function initDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  logger.info("dataLayer initialized");
}

/**
 * Push event to GTM dataLayer
 */
export function pushToDataLayer(event: Record<string, any>) {
  if (typeof window === "undefined") {
    logger.warn("Cannot push to dataLayer: window undefined");
    return;
  }

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  window.dataLayer.push(event);
  logger.info("Pushed to dataLayer", { event: event.event });
}

/**
 * Push a GA4-compatible custom event to GTM. GTM is the only browser-side
 * delivery path; the container owns forwarding to GA4 and Google Ads.
 */
function trackGa4Event(eventName: string, properties?: Record<string, any>) {
  pushToDataLayer({
    event: eventName,
    ...properties,
  });
}

/**
 * Track purchase event with GA4 enhanced ecommerce format
 */
export function trackPurchaseEvent(params: {
  transactionId: string;
  value: number;
  currency: string;
  items: Array<{
    itemId: string;
    itemName: string;
    price: number;
    quantity: number;
  }>;
  userEmail?: string;
  userPhone?: string;
}) {
  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: params.transactionId,
      value: params.value,
      currency: params.currency,
      items: params.items.map((item) => ({
        item_id: item.itemId,
        item_name: item.itemName,
        price: item.price,
        quantity: item.quantity,
      })),
    },
    // Enhanced conversion data (will be hashed by GTM tags)
    user_data: {
      email_address: params.userEmail,
      phone_number: params.userPhone,
    },
  });
}

/**
 * Track a RevenueCat-confirmed browser purchase result. The webhook remains
 * authoritative for Google Ads bidding; this keeps GA4/GTM reporting aligned
 * when the client is still present after RevenueCat returns success.
 */
export function trackRevenueCatConversionEvent(params: {
  currency: string;
  isTrial: boolean;
  itemId: string;
  itemName: string;
  packageId?: string;
  paywallSource?: string;
  source: string;
  transactionId: string;
  userEmail?: string;
  value: number;
}) {
  const reportedValue = params.isTrial && params.value <= 0 ? 5 : params.value;
  const items = [
    {
      item_id: params.itemId,
      item_name: params.itemName,
      item_category: "subscription",
      price: reportedValue,
      quantity: 1,
    },
  ];

  const baseProperties = {
    transaction_id: params.transactionId,
    value: reportedValue,
    currency: params.currency,
    items,
    package_id: params.packageId,
    source: params.source,
    paywall_source: normalizePaywallSource(params.paywallSource || params.source),
    user_data: {
      email_address: params.userEmail,
    },
  };

  if (params.isTrial) {
    trackGa4Event("trial_started", {
      ...baseProperties,
      trial_value: reportedValue,
    });
  } else {
    trackGa4Event("purchase", baseProperties);
  }

  trackGa4Event("subscription_activated", {
    ...baseProperties,
    is_trial: params.isTrial,
  });
}

/**
 * Track signup event
 */
export function trackSignupEvent(params: {
  method: "email" | "google" | "facebook";
  userEmail?: string;
}) {
  pushToDataLayer({
    event: "sign_up",
    method: params.method,
    user_data: {
      email_address: params.userEmail,
    },
  });
}

/**
 * Track begin_checkout event
 */
export function trackBeginCheckout(params: {
  value: number;
  currency: string;
  surface: PaywallSurface;
  offerMode?: string;
  paywallSource?: string;
  userType?: "guest" | "authenticated";
  items: Array<{
    itemId: string;
    itemName: string;
    price: number;
  }>;
}) {
  const items = params.items.map((item) => ({
    item_id: item.itemId,
    item_name: item.itemName,
    price: item.price,
  }));

  pushToDataLayer({
    event: "begin_checkout",
    checkout_surface: params.surface,
    paywall_source: normalizePaywallSource(params.paywallSource || params.surface),
    offer_mode: params.offerMode,
    user_type: params.userType,
    ecommerce: {
      value: params.value,
      currency: params.currency,
      items,
    },
  });
}

/**
 * Track paywall/pricing exposure as an early monetization intent signal.
 */
export function trackPaywallView(params: {
  surface: PaywallSurface;
  source?: string;
  userType?: "guest" | "authenticated";
  offerMode?: string;
  userIntentSegment?: string;
}) {
  const paywallSourceProperties = buildPaywallSourceProperties(params.source);
  trackGa4Event("paywall_view", {
    paywall_surface: params.surface,
    ...paywallSourceProperties,
    user_type: params.userType,
    offer_mode: params.offerMode,
    user_intent_segment: params.userIntentSegment,
  });
}

/**
 * Track every successful speaking session start.
 */
export function trackSpeakingSessionStarted(params?: {
  sessionMode?: string;
  userType?: "guest" | "authenticated";
  selectedLanguageCode?: string;
  selectedLanguageName?: string;
  selectedLanguageLevel?: string;
}) {
  trackGa4Event("speaking_session_started", {
    session_mode: params?.sessionMode,
    user_type: params?.userType,
    selected_language_code: params?.selectedLanguageCode,
    selected_language_name: params?.selectedLanguageName,
    selected_language_level: params?.selectedLanguageLevel,
  });
}

/**
 * Track the first meaningful completed conversation once per browser.
 */
export function trackFirstConversationCompleted(params?: {
  durationSeconds?: number;
  sessionMode?: string;
  userType?: "guest" | "authenticated";
  selectedLanguageCode?: string;
  selectedLanguageName?: string;
}) {
  trackGa4Event("first_conversation_completed", {
    session_duration_seconds: params?.durationSeconds,
    session_mode: params?.sessionMode,
    user_type: params?.userType,
    selected_language_code: params?.selectedLanguageCode,
    selected_language_name: params?.selectedLanguageName,
  });
}

/**
 * Track a strict long-session threshold as a higher-quality engagement signal.
 */
export function trackLongSession5Min(params?: {
  durationMinutes?: number;
  sessionMode?: string;
  userType?: "guest" | "authenticated";
  selectedLanguageCode?: string;
  selectedLanguageName?: string;
}) {
  trackGa4Event("long_session_5min", {
    duration_minutes: params?.durationMinutes ?? 5,
    session_mode: params?.sessionMode,
    user_type: params?.userType,
    selected_language_code: params?.selectedLanguageCode,
    selected_language_name: params?.selectedLanguageName,
  });
}

/**
 * Track add_to_cart event (for subscription selection)
 */
export function trackAddToCart(params: {
  itemId: string;
  itemName: string;
  price: number;
  currency: string;
}) {
  pushToDataLayer({
    event: "add_to_cart",
    ecommerce: {
      currency: params.currency,
      value: params.price,
      items: [
        {
          item_id: params.itemId,
          item_name: params.itemName,
          price: params.price,
          quantity: 1,
        },
      ],
    },
  });
}

/**
 * Track custom event
 */
export function trackCustomEvent(eventName: string, properties?: Record<string, any>) {
  pushToDataLayer({
    event: eventName,
    ...properties,
  });
}

/**
 * Set user properties in dataLayer
 */
export function setUserProperties(properties: Record<string, any>) {
  pushToDataLayer({
    event: "set_user_properties",
    user_properties: properties,
  });
}
